<template>
  <div class="stock-admin-shell" role="region" aria-labelledby="stock-admin-title">
    <div class="stock-admin-container">
      <header class="operations-header" aria-labelledby="stock-admin-title">
        <div class="operations-header__top">
          <div class="operations-header__copy">
            <div class="eyebrow">
              <span class="eyebrow__mark" aria-hidden="true"></span>
              Operations · Marketing-Lager
            </div>
            <h1 id="stock-admin-title" class="operations-title">Lagersteuerung</h1>
            <p class="operations-subtitle">Produkte, Bestände und Entnahmen zentral im Blick.</p>
          </div>

          <v-btn to="/dashboard/communication/stock" outlined class="secondary-action header-back-button">
            <v-icon left aria-hidden="true">mdi-arrow-left</v-icon>
            Zur Entnahme
          </v-btn>
        </div>

        <dl class="kpi-grid" aria-label="Lagerübersicht">
          <div class="kpi-card">
            <dt class="kpi-card__label">Gesamt</dt>
            <dd class="kpi-card__value">
              {{ productStats.total }}
              <span class="kpi-card__meta">Produkte</span>
            </dd>
          </div>
          <div class="kpi-card kpi-card--active">
            <dt class="kpi-card__label">Aktiv</dt>
            <dd class="kpi-card__value">
              {{ productStats.active }}
              <span class="kpi-card__meta">sichtbar</span>
            </dd>
          </div>
          <div class="kpi-card kpi-card--low">
            <dt class="kpi-card__label">Knapp</dt>
            <dd class="kpi-card__value">
              {{ productStats.low }}
              <span class="kpi-card__meta">unter Ziel</span>
            </dd>
          </div>
          <div class="kpi-card kpi-card--empty">
            <dt class="kpi-card__label">Leer</dt>
            <dd class="kpi-card__value">
              {{ productStats.empty }}
              <span class="kpi-card__meta">Handlungsbedarf</span>
            </dd>
          </div>
        </dl>
      </header>

      <nav class="segmented-nav" aria-label="Lagerbereiche">
        <v-tabs v-model="activeTab" background-color="transparent" dark hide-slider grow class="operations-tabs">
          <v-tab class="operations-tab text-none">
            <v-icon left aria-hidden="true">mdi-package-variant-closed</v-icon>
            Produkte
            <span class="tab-count">{{ products.length }}</span>
          </v-tab>
          <v-tab class="operations-tab text-none">
            <v-icon left aria-hidden="true">mdi-swap-horizontal</v-icon>
            Entnahmen
            <span v-if="orders.length" class="tab-count">{{ orders.length }}</span>
          </v-tab>
        </v-tabs>
      </nav>

      <v-tabs-items v-model="activeTab" dark class="operations-tab-items">
        <v-tab-item>
          <section class="content-panel" aria-labelledby="products-heading">
            <div class="section-heading-row">
              <div>
                <p class="section-kicker">Sortiment</p>
                <h2 id="products-heading" class="section-title">Produkte verwalten</h2>
              </div>
              <span class="result-count" aria-live="polite">
                {{ filteredProducts.length }} {{ filteredProducts.length === 1 ? "Eintrag" : "Einträge" }}
              </span>
            </div>

            <div class="product-toolbar">
              <v-text-field
                v-model="productSearch"
                dark
                label="Produkte durchsuchen"
                placeholder="Titel, Barcode oder Kategorie"
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                clearable
                class="operations-search"
                aria-label="Produkte durchsuchen"></v-text-field>

              <v-btn color="primary" depressed class="primary-action product-create-button" @click="openCreateProductDialog">
                <v-icon left aria-hidden="true">mdi-plus</v-icon>
                Neues Produkt
              </v-btn>
            </div>

            <v-data-table
              :headers="productHeaders"
              :items="filteredProducts"
              :loading="loadingProducts"
              dark
              :items-per-page="15"
              class="operations-table desktop-table"
              :no-data-text="productTableEmptyText"
              loading-text="Produkte werden geladen …">
              <template v-slot:item.barcode="{ item }">
                <span class="barcode-value">
                  <v-icon x-small aria-hidden="true">mdi-barcode</v-icon>
                  {{ item.barcode }}
                </span>
              </template>

              <template v-slot:item.title="{ item }">
                <span class="table-primary-text">{{ item.title }}</span>
              </template>

              <template v-slot:item.stock="{ item }">
                <span class="status-label" :class="getStockStatusClass(item)">
                  {{ getStockStatusLabel(item) }}
                </span>
              </template>

              <template v-slot:item.notification="{ item }">
                <div v-if="item.notificationThreshold !== null" class="notification-cell">
                  <span>Ziel: {{ item.notificationThreshold }}</span>
                  <span>{{ item.notificationEmail }}</span>
                </div>
                <span v-else class="cell-muted">Nicht aktiv</span>
              </template>

              <template v-slot:item.active="{ item }">
                <span class="status-label" :class="item.active ? 'status-label--active' : 'status-label--inactive'">
                  {{ item.active ? "Aktiv" : "Inaktiv" }}
                </span>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-actions">
                  <v-btn
                    icon
                    class="touch-icon-button"
                    :aria-label="`${item.title} bearbeiten`"
                    :title="`${item.title} bearbeiten`"
                    @click="openEditProductDialog(item)">
                    <v-icon small aria-hidden="true">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="item.active"
                    icon
                    class="touch-icon-button touch-icon-button--danger"
                    :aria-label="`${item.title} deaktivieren`"
                    :title="`${item.title} deaktivieren`"
                    @click="confirmDeleteProduct(item)">
                    <v-icon small aria-hidden="true">mdi-archive-arrow-down-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="mobile-card-list" aria-live="polite">
              <div v-if="loadingProducts" class="loading-state" role="status">
                <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
                <span>Produkte werden geladen …</span>
              </div>
              <div v-else-if="filteredProducts.length === 0" class="empty-state">
                <v-icon aria-hidden="true">mdi-package-variant-closed-remove</v-icon>
                <strong>Keine Produkte gefunden</strong>
                <span>Prüfe den Suchbegriff oder erfasse ein neues Produkt.</span>
              </div>
              <template v-else>
                <article v-for="product in filteredProducts" :key="product._id" class="mobile-data-card">
                  <div class="mobile-data-card__header">
                    <div class="mobile-data-card__title-wrap">
                      <span class="mobile-data-card__eyebrow">{{ product.category || "Allgemein" }}</span>
                      <h3 class="mobile-data-card__title">{{ product.title }}</h3>
                      <span class="barcode-value">
                        <v-icon x-small aria-hidden="true">mdi-barcode</v-icon>
                        {{ product.barcode }}
                      </span>
                    </div>
                    <span class="status-label" :class="product.active ? 'status-label--active' : 'status-label--inactive'">
                      {{ product.active ? "Aktiv" : "Inaktiv" }}
                    </span>
                  </div>

                  <dl class="mobile-data-grid">
                    <div>
                      <dt>Bestand</dt>
                      <dd>
                        <span class="status-label" :class="getStockStatusClass(product)">{{ getStockStatusLabel(product) }}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Mindestbestand</dt>
                      <dd>{{ product.notificationThreshold !== null ? product.notificationThreshold : "Nicht aktiv" }}</dd>
                    </div>
                  </dl>

                  <div class="mobile-card-actions">
                    <v-btn text class="mobile-action-button" :aria-label="`${product.title} bearbeiten`" @click="openEditProductDialog(product)">
                      <v-icon left small aria-hidden="true">mdi-pencil-outline</v-icon>
                      Bearbeiten
                    </v-btn>
                    <v-btn
                      v-if="product.active"
                      icon
                      class="touch-icon-button touch-icon-button--danger"
                      :aria-label="`${product.title} deaktivieren`"
                      :title="`${product.title} deaktivieren`"
                      @click="confirmDeleteProduct(product)">
                      <v-icon small aria-hidden="true">mdi-archive-arrow-down-outline</v-icon>
                    </v-btn>
                  </div>
                </article>
              </template>
            </div>
          </section>
        </v-tab-item>

        <v-tab-item>
          <section class="content-panel" aria-labelledby="orders-heading">
            <div class="section-heading-row">
              <div>
                <p class="section-kicker">Bewegungen</p>
                <h2 id="orders-heading" class="section-title">Entnahmeprotokoll</h2>
              </div>
              <span class="result-count" aria-live="polite">
                {{ filteredOrders.length }} {{ filteredOrders.length === 1 ? "Entnahme" : "Entnahmen" }}
              </span>
            </div>

            <div class="history-toolbar">
              <v-text-field
                v-model="historySearch"
                dark
                label="Entnahmen durchsuchen"
                placeholder="Produkt, Person, Studio oder Kommentar"
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                clearable
                class="operations-search history-search"
                aria-label="Entnahmen durchsuchen"></v-text-field>

              <v-btn
                v-if="activeFilterCount > 0"
                text
                class="reset-filter-button"
                aria-label="Alle aktiven Filter zurücksetzen"
                @click="resetFilters">
                <v-icon left small aria-hidden="true">mdi-filter-remove-outline</v-icon>
                Filter zurücksetzen
                <span class="filter-count">{{ activeFilterCount }}</span>
              </v-btn>
            </div>

            <div class="filter-panel" aria-label="Entnahmen filtern">
              <div class="filter-panel__label">
                <v-icon small aria-hidden="true">mdi-tune-variant</v-icon>
                Filter
              </div>
              <div class="filter-grid">
                <v-select
                  v-model="filters.createdBy"
                  dark
                  :items="personFilterOptions"
                  label="Person"
                  dense
                  outlined
                  hide-details
                  class="operations-select"
                  :menu-props="{ contentClass: 'stock-admin-menu' }"></v-select>
                <v-select
                  v-model="filters.category"
                  dark
                  :items="categoryFilterOptions"
                  label="Kategorie"
                  dense
                  outlined
                  hide-details
                  class="operations-select"
                  :menu-props="{ contentClass: 'stock-admin-menu' }"></v-select>
                <v-select
                  v-model="filters.product"
                  dark
                  :items="productFilterOptions"
                  label="Produkt"
                  dense
                  outlined
                  hide-details
                  class="operations-select"
                  :menu-props="{ contentClass: 'stock-admin-menu' }"></v-select>
                <v-select
                  v-model="filters.studio"
                  dark
                  :items="studioFilterOptions"
                  label="Studio"
                  dense
                  outlined
                  hide-details
                  class="operations-select"
                  :menu-props="{ contentClass: 'stock-admin-menu' }"></v-select>
              </div>
            </div>

            <v-data-table
              :headers="orderHeaders"
              :items="filteredOrders"
              :loading="loadingOrders"
              dark
              :items-per-page="20"
              class="operations-table desktop-table"
              :no-data-text="orderTableEmptyText"
              loading-text="Entnahmeprotokoll wird geladen …">
              <template v-slot:item.createdAt="{ item }">
                <time class="date-cell" :datetime="item.createdAt">{{ formatDateTime(item.createdAt) }}</time>
              </template>

              <template v-slot:item.createdBy="{ item }">
                <span class="table-primary-text">{{ getOrderPersonName(item) }}</span>
              </template>

              <template v-slot:item.productTitle="{ item }">
                <div class="product-cell">
                  <span class="table-primary-text">{{ item.productTitle }}</span>
                  <span class="barcode-value">
                    <v-icon x-small aria-hidden="true">mdi-barcode</v-icon>
                    {{ item.productBarcode }}
                  </span>
                </div>
              </template>

              <template v-slot:item.studio="{ item }">
                <span>{{ getOrderStudioName(item) }}</span>
              </template>

              <template v-slot:item.quantity="{ item }">
                <span class="quantity-label">{{ item.quantity }}</span>
              </template>

              <template v-slot:item.comment="{ item }">
                <span v-if="item.comment" class="comment-cell" :title="item.comment">
                  {{ truncateText(item.comment, 40) }}
                </span>
                <span v-else class="cell-muted">Kein Kommentar</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-actions">
                  <v-btn
                    icon
                    class="touch-icon-button"
                    :aria-label="`Menge für ${item.productTitle} bearbeiten`"
                    :title="`Menge für ${item.productTitle} bearbeiten`"
                    @click="openEditOrderDialog(item)">
                    <v-icon small aria-hidden="true">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="touch-icon-button touch-icon-button--danger"
                    :aria-label="`Entnahme von ${item.productTitle} stornieren`"
                    :title="`Entnahme von ${item.productTitle} stornieren`"
                    @click="confirmCancelOrder(item)">
                    <v-icon small aria-hidden="true">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="mobile-card-list" aria-live="polite">
              <div v-if="loadingOrders" class="loading-state" role="status">
                <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
                <span>Entnahmen werden geladen …</span>
              </div>
              <div v-else-if="filteredOrders.length === 0" class="empty-state">
                <v-icon aria-hidden="true">mdi-swap-horizontal-circle-outline</v-icon>
                <strong>Keine Entnahmen gefunden</strong>
                <span>Ändere die Suche oder setze die aktiven Filter zurück.</span>
              </div>
              <template v-else>
                <article v-for="order in filteredOrders" :key="order._id" class="mobile-data-card mobile-order-card">
                  <div class="mobile-data-card__header">
                    <div class="mobile-data-card__title-wrap">
                      <span class="mobile-data-card__eyebrow">{{ order.productCategory || "Allgemein" }}</span>
                      <h3 class="mobile-data-card__title">{{ order.productTitle }}</h3>
                      <span class="barcode-value">
                        <v-icon x-small aria-hidden="true">mdi-barcode</v-icon>
                        {{ order.productBarcode }}
                      </span>
                    </div>
                    <span class="quantity-label" :aria-label="`${order.quantity} Stück`">×{{ order.quantity }}</span>
                  </div>

                  <dl class="mobile-data-grid mobile-data-grid--orders">
                    <div>
                      <dt>Zeitpunkt</dt>
                      <dd>
                        <time :datetime="order.createdAt">{{ formatDateTime(order.createdAt) }}</time>
                      </dd>
                    </div>
                    <div>
                      <dt>Person</dt>
                      <dd>{{ getOrderPersonName(order) }}</dd>
                    </div>
                    <div>
                      <dt>Studio</dt>
                      <dd>{{ getOrderStudioName(order) }}</dd>
                    </div>
                  </dl>

                  <p v-if="order.comment" class="mobile-comment">{{ order.comment }}</p>

                  <div class="mobile-card-actions">
                    <v-btn
                      text
                      class="mobile-action-button"
                      :aria-label="`Menge für ${order.productTitle} bearbeiten`"
                      @click="openEditOrderDialog(order)">
                      <v-icon left small aria-hidden="true">mdi-pencil-outline</v-icon>
                      Menge ändern
                    </v-btn>
                    <v-btn
                      icon
                      class="touch-icon-button touch-icon-button--danger"
                      :aria-label="`Entnahme von ${order.productTitle} stornieren`"
                      :title="`Entnahme von ${order.productTitle} stornieren`"
                      @click="confirmCancelOrder(order)">
                      <v-icon small aria-hidden="true">mdi-close-circle-outline</v-icon>
                    </v-btn>
                  </div>
                </article>
              </template>
            </div>
          </section>
        </v-tab-item>
      </v-tabs-items>

      <v-dialog
        v-model="productDialogOpen"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="720"
        persistent
        scrollable
        transition="dialog-bottom-transition"
        class="product-form-dialog"
        aria-labelledby="product-dialog-title">
        <v-card dark class="dialog-card product-form-card">
          <v-card-title class="dialog-header">
            <div class="dialog-title-group">
              <span class="dialog-icon" aria-hidden="true">
                <v-icon>{{ isEditMode ? "mdi-pencil-outline" : "mdi-package-variant-plus" }}</v-icon>
              </span>
              <div>
                <span class="dialog-eyebrow">Produktstamm</span>
                <h2 id="product-dialog-title" class="dialog-title">
                  {{ isEditMode ? "Produkt bearbeiten" : "Neues Produkt" }}
                </h2>
              </div>
            </div>
            <v-btn
              icon
              class="touch-icon-button dialog-close-button"
              aria-label="Produktformular schließen"
              title="Schließen"
              @click="closeProductDialog">
              <v-icon aria-hidden="true">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="product-dialog-body">
            <v-form ref="productForm" v-model="productFormValid">
              <section class="form-section" aria-labelledby="base-data-title">
                <div class="form-section__header">
                  <span class="form-section__number">01</span>
                  <div>
                    <h3 id="base-data-title">Basisdaten</h3>
                    <p>Name, Kategorie und eindeutige Identifikation.</p>
                  </div>
                </div>

                <div class="form-grid">
                  <v-text-field
                    v-model="productFormModel.title"
                    label="Produkttitel *"
                    outlined
                    color="primary"
                    class="form-field form-grid__full"
                    :rules="[(v) => !!v || 'Titel ist erforderlich']"></v-text-field>

                  <div class="barcode-field-row form-grid__full">
                    <v-text-field
                      v-model="productFormModel.barcode"
                      label="Barcode *"
                      outlined
                      color="primary"
                      class="form-field barcode-input"
                      :rules="[(v) => !!v || 'Barcode ist erforderlich']"
                      hint="EAN eingeben, generieren, live scannen oder aus einem Bild lesen."
                      persistent-hint></v-text-field>
                    <div class="barcode-actions">
                      <v-btn
                        icon
                        class="touch-icon-button field-action-button"
                        aria-label="Zufälligen Barcode generieren"
                        title="Barcode generieren"
                        @click="generateRandomBarcode">
                        <v-icon aria-hidden="true">mdi-auto-fix</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="touch-icon-button field-action-button field-action-button--accent"
                        aria-label="Barcode live scannen"
                        title="Live scannen"
                        @click="openAdminLiveScanner">
                        <v-icon aria-hidden="true">mdi-line-scan</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="touch-icon-button field-action-button"
                        aria-label="Barcode aus einem Bild lesen"
                        title="Aus Bild lesen"
                        :loading="scanningFile"
                        @click="triggerAdminCaptureFlow">
                        <v-icon aria-hidden="true">mdi-image-search-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <input
                    ref="adminFileInput"
                    type="file"
                    accept="image/*"
                    class="visually-hidden-input"
                    tabindex="-1"
                    @change="onAdminFileSelected" />

                  <v-combobox
                    v-model="productFormModel.category"
                    :items="categories"
                    label="Kategorie"
                    outlined
                    color="primary"
                    clearable
                    hint="Bestehende Kategorie wählen oder eine neue eingeben."
                    persistent-hint
                    class="form-field form-grid__full"
                    :menu-props="{ contentClass: 'stock-admin-menu' }"></v-combobox>
                </div>
              </section>

              <section class="form-section" aria-labelledby="inventory-data-title">
                <div class="form-section__header">
                  <span class="form-section__number">02</span>
                  <div>
                    <h3 id="inventory-data-title">Bestand</h3>
                    <p>Bestandsführung und automatische Warnung konfigurieren.</p>
                  </div>
                </div>

                <div class="switch-row">
                  <div>
                    <strong>Bestand überwachen</strong>
                    <span>Entnahmen werden vom aktuellen Lagerbestand abgezogen.</span>
                  </div>
                  <v-switch v-model="trackStock" inset hide-details color="primary" aria-label="Bestand überwachen"></v-switch>
                </div>

                <div v-if="trackStock" class="form-grid form-grid--spaced">
                  <v-text-field
                    v-model.number="productFormModel.stock"
                    type="number"
                    label="Aktueller Lagerbestand"
                    outlined
                    color="primary"
                    class="form-field"
                    :rules="[
                      (v) => (v !== undefined && v !== '' && v !== null) || 'Bitte Lagerbestand eingeben',
                      (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
                    ]"></v-text-field>

                  <div class="switch-row switch-row--nested">
                    <div>
                      <strong>Mindestbestand melden</strong>
                      <span>E-Mail senden, wenn der Zielwert unterschritten wird.</span>
                    </div>
                    <v-switch
                      v-model="enableNotification"
                      inset
                      hide-details
                      color="primary"
                      aria-label="Mindestbestand per E-Mail melden"></v-switch>
                  </div>

                  <template v-if="enableNotification">
                    <v-text-field
                      v-model.number="productFormModel.notificationThreshold"
                      type="number"
                      label="Mindestbestand"
                      outlined
                      color="primary"
                      class="form-field"
                      :rules="[(v) => (v !== undefined && v !== '' && v !== null) || 'Bitte Grenzwert eingeben']"></v-text-field>
                    <v-text-field
                      v-model="productFormModel.notificationEmail"
                      type="email"
                      label="Benachrichtigungs-E-Mail"
                      outlined
                      color="primary"
                      class="form-field"
                      :rules="[
                        (v) => !!v || 'Bitte E-Mail-Adresse eingeben',
                        (v) => /.+@.+\..+/.test(v) || 'Bitte gültige E-Mail-Adresse eingeben',
                      ]"></v-text-field>
                  </template>
                </div>
              </section>

              <section class="form-section" aria-labelledby="visibility-title">
                <div class="form-section__header">
                  <span class="form-section__number">03</span>
                  <div>
                    <h3 id="visibility-title">Sichtbarkeit</h3>
                    <p>Steuert, ob das Produkt in der Entnahmeansicht erscheint.</p>
                  </div>
                </div>

                <div class="switch-row">
                  <div>
                    <strong>Produkt aktiv</strong>
                    <span>{{ productFormModel.active ? "Für Entnahmen verfügbar" : "Nur im Produktstamm sichtbar" }}</span>
                  </div>
                  <v-switch v-model="productFormModel.active" inset hide-details color="primary" aria-label="Produkt aktiv"></v-switch>
                </div>
              </section>
            </v-form>
          </v-card-text>

          <v-card-actions class="dialog-actions dialog-actions--sticky">
            <v-btn text class="dialog-secondary-button" @click="closeProductDialog">Abbrechen</v-btn>
            <v-btn
              color="primary"
              depressed
              class="dialog-primary-button"
              :loading="savingProduct"
              :disabled="!productFormValid"
              @click="saveProduct">
              <v-icon left small aria-hidden="true">mdi-check</v-icon>
              {{ isEditMode ? "Änderungen speichern" : "Produkt anlegen" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="adminLiveScannerOpen"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="720"
        persistent
        transition="dialog-bottom-transition"
        class="responsive-dialog"
        aria-labelledby="admin-scanner-dialog-title">
        <v-card dark class="dialog-card admin-scanner-card">
          <v-card-title class="dialog-header">
            <div class="dialog-title-group">
              <span class="dialog-icon" aria-hidden="true"><v-icon>mdi-line-scan</v-icon></span>
              <div>
                <span class="dialog-eyebrow">Produkt identifizieren</span>
                <h2 id="admin-scanner-dialog-title" class="dialog-title">Barcode live scannen</h2>
              </div>
            </div>
            <v-btn
              icon
              class="touch-icon-button dialog-close-button"
              aria-label="Live-Scanner schließen"
              title="Schließen"
              @click="closeAdminLiveScanner">
              <v-icon aria-hidden="true">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="admin-scanner-body">
            <div class="admin-scanner-container">
              <video ref="adminScannerVideo" class="admin-live-qr-reader" autoplay muted playsinline></video>
              <canvas ref="adminScannerCanvas" class="visually-hidden-input" aria-hidden="true"></canvas>
              <div class="admin-scanner-frame" aria-hidden="true"></div>
              <div class="admin-scanner-laser" aria-hidden="true"></div>
            </div>
            <div class="admin-scanner-tip">
              <v-icon small aria-hidden="true">mdi-lightbulb-outline</v-icon>
              <span>Barcode mit sichtbarem weissem Rand ruhig halten – nicht zu nah. Ideal sind etwa 60–85 % der Rahmenbreite.</span>
            </div>
            <section class="admin-scanner-debug" aria-labelledby="admin-scanner-debug-title">
              <div class="admin-scanner-debug__header">
                <span id="admin-scanner-debug-title">Live-Diagnose</span>
                <span class="admin-scanner-status">{{ adminScannerStatus }}</span>
              </div>
              <ol class="admin-scanner-log" aria-live="polite" aria-relevant="additions">
                <li v-for="entry in adminScannerLogs" :key="entry.id" :class="`admin-scanner-log__entry--${entry.type}`">
                  <time>{{ entry.time }}</time>
                  <span>{{ entry.message }}</span>
                </li>
              </ol>
            </section>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="deleteDialogOpen"
        max-width="460"
        transition="dialog-bottom-transition"
        class="responsive-dialog"
        aria-labelledby="delete-product-dialog-title">
        <v-card dark class="dialog-card confirmation-card">
          <v-card-title class="confirmation-header">
            <span class="confirmation-icon confirmation-icon--warning" aria-hidden="true">
              <v-icon>mdi-archive-arrow-down-outline</v-icon>
            </span>
            <div>
              <span class="dialog-eyebrow">Sichtbarkeit ändern</span>
              <h2 id="delete-product-dialog-title" class="dialog-title">Produkt deaktivieren?</h2>
            </div>
          </v-card-title>
          <v-card-text v-if="productToDelete" class="confirmation-copy">
            <strong>{{ productToDelete.title }}</strong> wird aus der Entnahmeansicht entfernt. Bestehende Einträge bleiben im Entnahmeprotokoll
            vollständig erhalten.
          </v-card-text>
          <v-card-actions class="dialog-actions confirmation-actions">
            <v-btn text class="dialog-secondary-button" @click="closeDeleteDialog">Abbrechen</v-btn>
            <v-btn color="error" depressed class="dialog-danger-button" :loading="deletingProduct" @click="executeDeleteProduct">
              Deaktivieren
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="editOrderDialogOpen"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="480"
        persistent
        scrollable
        transition="dialog-bottom-transition"
        class="responsive-dialog"
        aria-labelledby="edit-order-dialog-title">
        <v-card dark class="dialog-card compact-form-card">
          <v-card-title class="dialog-header">
            <div class="dialog-title-group">
              <span class="dialog-icon" aria-hidden="true"><v-icon>mdi-swap-horizontal</v-icon></span>
              <div>
                <span class="dialog-eyebrow">Entnahmeprotokoll</span>
                <h2 id="edit-order-dialog-title" class="dialog-title">Menge bearbeiten</h2>
              </div>
            </div>
            <v-btn icon class="touch-icon-button dialog-close-button" aria-label="Dialog schließen" title="Schließen" @click="closeEditOrderDialog">
              <v-icon aria-hidden="true">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text v-if="orderToEdit" class="compact-form-body">
            <div class="order-summary">
              <span>{{ orderToEdit.productTitle }}</span>
              <small>{{ getOrderStudioName(orderToEdit) }}</small>
            </div>
            <v-text-field
              v-model.number="orderFormModel.quantity"
              type="number"
              label="Neue Menge *"
              outlined
              min="1"
              color="primary"
              class="form-field"
              :rules="[
                (v) => !!v || 'Bitte Menge eingeben',
                (v) => Number(v) > 0 || 'Menge muss grösser als 0 sein',
                (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
              ]"></v-text-field>
            <p class="dialog-note">
              <v-icon small aria-hidden="true">mdi-information-outline</v-icon>
              Die Differenz wird automatisch mit dem Lagerbestand verrechnet.
            </p>
          </v-card-text>
          <v-card-actions class="dialog-actions dialog-actions--sticky">
            <v-btn text class="dialog-secondary-button" @click="closeEditOrderDialog">Abbrechen</v-btn>
            <v-btn
              color="primary"
              depressed
              class="dialog-primary-button"
              :loading="savingOrderEdit"
              :disabled="!orderFormModel.quantity || orderFormModel.quantity <= 0 || !Number.isInteger(Number(orderFormModel.quantity))"
              @click="saveOrderEdit">
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="cancelOrderDialogOpen"
        max-width="460"
        persistent
        transition="dialog-bottom-transition"
        class="responsive-dialog"
        aria-labelledby="cancel-order-dialog-title">
        <v-card dark class="dialog-card confirmation-card">
          <v-card-title class="confirmation-header">
            <span class="confirmation-icon confirmation-icon--danger" aria-hidden="true">
              <v-icon>mdi-close-circle-outline</v-icon>
            </span>
            <div>
              <span class="dialog-eyebrow">Bestand korrigieren</span>
              <h2 id="cancel-order-dialog-title" class="dialog-title">Entnahme stornieren?</h2>
            </div>
          </v-card-title>
          <v-card-text v-if="orderToCancel" class="confirmation-copy">
            Die Entnahme von <strong>{{ orderToCancel.quantity }} × {{ orderToCancel.productTitle }}</strong> für
            <strong>{{ getOrderStudioName(orderToCancel) }}</strong> wird entfernt. Der Bestand wird automatisch gutgeschrieben.
          </v-card-text>
          <v-card-actions class="dialog-actions confirmation-actions">
            <v-btn text class="dialog-secondary-button" @click="closeCancelOrderDialog">Abbrechen</v-btn>
            <v-btn color="error" depressed class="dialog-danger-button" :loading="cancelingOrder" @click="executeCancelOrder">
              Entnahme stornieren
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import api from "@/api"
import moment from "moment"

const LOCAL_BARCODE_SCRIPT_URL = "https://app.wellcomefit.ch/img/js/barcode.js"
const LOCAL_BARCODE_WASM_URL = "/img/js/zxing_reader.wasm"
const LOCAL_ZXING_MODULE_OVERRIDES = Object.freeze({
  locateFile: (fileName, scriptDirectory) => (fileName.endsWith(".wasm") ? LOCAL_BARCODE_WASM_URL : `${scriptDirectory || ""}${fileName}`),
})

export default {
  name: "MarketingStockAdmin",
  data() {
    return {
      activeTab: 0,

      // Products Management Tab
      products: [],
      categories: [],
      loadingProducts: false,
      productSearch: "",
      productHeaders: [
        { text: "Titel", value: "title", sortable: true },
        { text: "Barcode", value: "barcode", sortable: true, width: "140px" },
        { text: "Kategorie", value: "category", sortable: true },
        { text: "Bestand", value: "stock", sortable: true, width: "130px" },
        { text: "Meldung", value: "notification", sortable: false },
        { text: "Sichtbarkeit", value: "active", sortable: true, width: "120px" },
        { text: "Aktionen", value: "actions", sortable: false, width: "112px", align: "end" },
      ],

      // Add/Edit Dialog
      productDialogOpen: false,
      isEditMode: false,
      productFormValid: false,
      savingProduct: false,
      scanningFile: false,
      adminLiveScannerOpen: false,
      adminScannerStream: null,
      adminScannerTimer: null,
      adminScannerDecodeBusy: false,
      adminScannerFrameIndex: 0,
      adminScannerSessionId: 0,
      adminScannerCandidate: "",
      adminScannerCandidateCount: 0,
      adminScannerCandidateLastSeenAt: 0,
      adminScannerStatus: "Bereit",
      adminScannerLogs: [],
      adminScannerLogId: 0,
      adminScannerFailureCount: 0,
      adminScannerLastFailureLogAt: 0,
      trackStock: false,
      enableNotification: false,
      productFormModel: {
        _id: null,
        barcode: "",
        title: "",
        category: "Allgemein",
        stock: 0,
        notificationThreshold: 5,
        notificationEmail: "da@wellcomefit.ch",
        active: true,
      },

      // Delete Dialog
      deleteDialogOpen: false,
      productToDelete: null,
      deletingProduct: false,

      // Audit Log Tab
      orders: [],
      loadingOrders: false,
      historySearch: "",
      filters: {
        createdBy: "all",
        category: "all",
        product: "all",
        studio: "all",
      },
      orderHeaders: [
        { text: "Zeitpunkt", value: "createdAt", sortable: true, width: "140px" },
        { text: "Person", value: "createdBy", sortable: true },
        { text: "Produkt", value: "productTitle", sortable: true },
        { text: "Kategorie", value: "productCategory", sortable: true },
        { text: "Studio", value: "studio", sortable: true },
        { text: "Menge", value: "quantity", sortable: true, width: "90px" },
        { text: "Kommentar", value: "comment", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, width: "100px", align: "end" },
      ],
      editOrderDialogOpen: false,
      orderToEdit: null,
      savingOrderEdit: false,
      orderFormModel: { quantity: 1 },
      cancelOrderDialogOpen: false,
      orderToCancel: null,
      cancelingOrder: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    productStats() {
      const activeProducts = this.products.filter((product) => product.active)
      return {
        total: this.products.length,
        active: activeProducts.length,
        low: activeProducts.filter((product) => this.isLowStock(product)).length,
        empty: activeProducts.filter((product) => product.stock !== null && Number(product.stock) <= 0).length,
      }
    },

    filteredProducts() {
      const query = (this.productSearch || "").trim().toLowerCase()
      if (!query) return this.products

      return this.products.filter((product) => {
        const searchValue = [product.title, product.barcode, product.category].filter(Boolean).join(" ").toLowerCase()
        return searchValue.includes(query)
      })
    },

    productTableEmptyText() {
      return (this.productSearch || "").trim() ? "Keine passenden Produkte gefunden" : "Keine Produkte erfasst"
    },

    activeFilterCount() {
      const selectFilterCount = Object.values(this.filters).filter((value) => value !== "all").length
      return selectFilterCount + ((this.historySearch || "").trim() ? 1 : 0)
    },

    orderTableEmptyText() {
      return this.activeFilterCount > 0 ? "Keine passenden Entnahmen gefunden" : "Keine Entnahmen vorhanden"
    },

    // Dropdown filters content generated dynamically from loaded orders
    personFilterOptions() {
      const options = [{ value: "all", text: "Alle Personen" }]
      const uniquePersons = new Map()

      this.orders.forEach((o) => {
        if (o.createdBy) {
          const id = o.createdBy._id
          const name = `${o.createdBy.vorname} ${o.createdBy.nachname}`
          uniquePersons.set(id, name)
        }
      })

      uniquePersons.forEach((name, id) => {
        options.push({ value: id, text: name })
      })

      return options
    },

    categoryFilterOptions() {
      const options = [{ value: "all", text: "Alle Kategorien" }]
      const uniqueCategories = new Set()

      this.orders.forEach((o) => {
        if (o.productCategory) uniqueCategories.add(o.productCategory)
      })

      uniqueCategories.forEach((cat) => {
        options.push({ value: cat, text: cat })
      })

      return options
    },

    productFilterOptions() {
      const options = [{ value: "all", text: "Alle Produkte" }]
      const uniqueProducts = new Map()

      this.orders.forEach((o) => {
        if (o.product) {
          uniqueProducts.set(o.product._id || o.product, o.productTitle)
        }
      })

      uniqueProducts.forEach((title, id) => {
        options.push({ value: id, text: title })
      })

      return options
    },

    studioFilterOptions() {
      const options = [{ value: "all", text: "Alle Studios" }]
      const studios = this.storeStudios || []

      studios
        .filter((s) => s.code && s.code !== "ot" && s.code !== "alibey")
        .forEach((s) => {
          options.push({ value: s._id, text: s.name })
        })

      return options
    },

    filteredOrders() {
      return this.orders.filter((o) => {
        const query = (this.historySearch || "").trim().toLowerCase()
        if (query) {
          const searchValue = [
            o.productTitle,
            o.productBarcode,
            o.productCategory,
            o.comment,
            o.createdBy?.vorname,
            o.createdBy?.nachname,
            o.studio?.name,
            this.formatDateTime(o.createdAt),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()

          if (!searchValue.includes(query)) return false
        }

        // User filter
        if (this.filters.createdBy !== "all") {
          const creatorId = o.createdBy?._id || o.createdBy
          if (creatorId !== this.filters.createdBy) return false
        }
        // Category filter
        if (this.filters.category !== "all") {
          if (o.productCategory !== this.filters.category) return false
        }
        // Product filter
        if (this.filters.product !== "all") {
          const prodId = o.product?._id || o.product
          if (prodId !== this.filters.product) return false
        }
        // Studio filter
        if (this.filters.studio !== "all") {
          const studId = o.studio?._id || o.studio
          if (studId !== this.filters.studio) return false
        }
        return true
      })
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 0) {
        this.loadProducts()
      } else if (newTab === 1) {
        this.loadOrders()
      }
    },
  },
  mounted() {
    this.loadProducts()
    this.loadCategories()
  },
  beforeDestroy() {
    this.closeAdminLiveScanner()
  },
  methods: {
    // --- LOAD DATA ---
    async loadProducts() {
      this.loadingProducts = true
      try {
        this.products = await api.marketingStock.fetchProducts({ all: true })
      } catch (err) {
        this.$toast.error("Produkte konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingProducts = false
      }
    },

    async loadCategories() {
      try {
        this.categories = await api.marketingStock.fetchCategories()
      } catch (err) {
        console.error("Fehler beim Laden der Kategorien:", err)
      }
    },

    async loadOrders() {
      this.loadingOrders = true
      try {
        this.orders = await api.marketingStock.fetchOrders()
      } catch (err) {
        this.$toast.error("Entnahmeprotokoll konnte nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingOrders = false
      }
    },

    // --- DIALOGS & ACTIONS ---
    openCreateProductDialog() {
      this.isEditMode = false
      this.trackStock = false
      this.enableNotification = false
      this.productFormModel = {
        _id: null,
        barcode: "",
        title: "",
        category: "Allgemein",
        stock: 0,
        notificationThreshold: 5,
        notificationEmail: "da@wellcomefit.ch",
        active: true,
      }
      this.productDialogOpen = true
      this.warmAdminBarcodeScanner()

      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
      })
    },

    openEditProductDialog(product) {
      this.isEditMode = true
      this.trackStock = product.stock !== null
      this.enableNotification = product.notificationThreshold !== null
      this.productFormModel = {
        _id: product._id,
        barcode: product.barcode,
        title: product.title,
        category: product.category || "Allgemein",
        stock: product.stock !== null ? product.stock : 0,
        notificationThreshold: product.notificationThreshold !== null ? product.notificationThreshold : 5,
        notificationEmail: product.notificationEmail || "da@wellcomefit.ch",
        active: product.active,
      }
      this.productDialogOpen = true
      this.warmAdminBarcodeScanner()

      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.validate()
      })
    },

    closeProductDialog() {
      if (this.adminLiveScannerOpen) this.closeAdminLiveScanner()
      this.productDialogOpen = false
    },

    async saveProduct() {
      if (!this.$refs.productForm.validate()) return

      this.savingProduct = true
      try {
        const payload = {
          barcode: this.productFormModel.barcode,
          title: this.productFormModel.title,
          category:
            typeof this.productFormModel.category === "object" && this.productFormModel.category !== null
              ? this.productFormModel.category.text || this.productFormModel.category.value
              : this.productFormModel.category,
          stock: this.trackStock ? Number(this.productFormModel.stock) : null,
          notificationThreshold: this.trackStock && this.enableNotification ? Number(this.productFormModel.notificationThreshold) : null,
          notificationEmail: this.trackStock && this.enableNotification ? this.productFormModel.notificationEmail : "da@wellcomefit.ch",
          active: this.productFormModel.active,
        }

        if (this.isEditMode) {
          await api.marketingStock.updateProduct(this.productFormModel._id, payload)
          this.$toast.success("Produkt erfolgreich aktualisiert")
        } else {
          await api.marketingStock.createProduct(payload)
          this.$toast.success("Produkt erfolgreich erstellt")
        }

        this.closeProductDialog()
        this.loadProducts()
        this.loadCategories()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Speichern des Produkts"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.savingProduct = false
      }
    },

    confirmDeleteProduct(product) {
      this.productToDelete = product
      this.deleteDialogOpen = true
    },

    closeDeleteDialog() {
      this.deleteDialogOpen = false
      this.productToDelete = null
    },

    async executeDeleteProduct() {
      if (!this.productToDelete) return

      this.deletingProduct = true
      try {
        await api.marketingStock.deleteProduct(this.productToDelete._id)
        this.$toast.success("Produkt erfolgreich deaktiviert")
        this.closeDeleteDialog()
        this.loadProducts()
      } catch (err) {
        this.$toast.error("Fehler beim Deaktivieren des Produkts")
        console.error(err)
      } finally {
        this.deletingProduct = false
      }
    },

    // --- HELPER METHODS ---
    resetFilters() {
      this.historySearch = ""
      this.filters = {
        createdBy: "all",
        category: "all",
        product: "all",
        studio: "all",
      }
    },

    isLowStock(product) {
      if (!product || product.stock === null || Number(product.stock) <= 0) return false
      const threshold =
        product.notificationThreshold !== null && product.notificationThreshold !== undefined ? Number(product.notificationThreshold) : 5
      return Number(product.stock) < threshold
    },

    getStockStatusClass(product) {
      if (!product || product.stock === null) return "status-label--unlimited"
      if (Number(product.stock) <= 0) return "status-label--empty"
      if (this.isLowStock(product)) return "status-label--low"
      return "status-label--available"
    },

    getStockStatusLabel(product) {
      if (!product || product.stock === null) return "Nicht gezählt"
      if (Number(product.stock) < 0) return `Fehlbestand · ${product.stock}`
      if (Number(product.stock) === 0) return "Leer · 0"
      if (this.isLowStock(product)) return `Knapp · ${product.stock}`
      return `Verfügbar · ${product.stock}`
    },

    getOrderPersonName(order) {
      if (!order || !order.createdBy || typeof order.createdBy !== "object") return "Unbekannt"
      return [order.createdBy.vorname, order.createdBy.nachname].filter(Boolean).join(" ") || "Unbekannt"
    },

    getOrderStudioName(order) {
      if (!order || !order.studio || typeof order.studio !== "object") return "–"
      return order.studio.name || "–"
    },

    generateRandomBarcode() {
      let code = ""
      do {
        let payload = "20"
        for (let i = 0; i < 10; i++) {
          payload += Math.floor(Math.random() * 10)
        }
        const weightedSum = payload.split("").reduce((sum, digit, index) => sum + Number(digit) * (index % 2 === 0 ? 1 : 3), 0)
        const checkDigit = (10 - (weightedSum % 10)) % 10
        code = `${payload}${checkDigit}`
      } while (this.products.some((product) => product.barcode === code))
      this.productFormModel.barcode = code
    },

    formatDateTime(d) {
      return d ? moment(d).format("DD.MM.YYYY HH:mm") : ""
    },

    truncateText(text, length) {
      if (!text) return ""
      if (text.length <= length) return text
      return text.substring(0, length) + "..."
    },

    // --- BARCODE SCANNING FOR ADMIN FORM ---
    getAdminReaderOptions(overrides = {}) {
      return {
        formats: ["EAN13", "Code128"],
        tryHarder: true,
        tryRotate: true,
        tryInvert: true,
        tryDownscale: false,
        minLineCount: 1,
        maxNumberOfSymbols: 1,
        ...overrides,
      }
    },

    warmAdminBarcodeScanner() {
      this.loadZxingWasm()
        .then(() => window.ZXingWASM.getZXingModule())
        .catch((error) => console.warn("Barcode-Scanner konnte nicht vorgewärmt werden:", error))
    },

    async openAdminLiveScanner() {
      const scannerSessionId = ++this.adminScannerSessionId
      this.resetAdminScannerConsensus()
      this.resetAdminScannerDiagnostics()
      this.adminLiveScannerOpen = true
      this.adminScannerStatus = "Initialisierung"
      this.addAdminScannerLog("Scanner wird initialisiert …")

      try {
        await this.loadZxingWasm()
        if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) return
        this.addAdminScannerLog("ZXing-C++ WebAssembly geladen · Formate: EAN-13 und Code 128", "success")
        this.addAdminScannerLog("Adaptiver Scan aktiv · schneller Fokusbereich, erweiterte Prüfung nur bei Bedarf")

        this.$nextTick(async () => {
          if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) return

          try {
            const stream = await this.openAdminCameraStream()
            if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) {
              stream.getTracks().forEach((track) => track.stop())
              return
            }

            this.adminScannerStream = stream
            const video = this.$refs.adminScannerVideo
            if (!video) throw new Error("Videoelement ist nicht verfügbar")
            video.srcObject = stream
            await video.play()
            await this.optimizeAdminScannerCamera(stream)

            this.adminScannerStatus = "Kamera aktiv"
            this.addAdminScannerLog("Kamera aktiv · ZXing-WASM sucht nach EAN-13 und Code 128", "success")
            this.scheduleAdminWasmScan(scannerSessionId, 0)
          } catch (startErr) {
            this.stopAdminCameraStream()
            if (scannerSessionId !== this.adminScannerSessionId) return
            this.adminScannerStatus = "Kamerafehler"
            this.addAdminScannerLog(`Kamera-Start fehlgeschlagen · ${this.formatAdminScannerError(startErr)}`, "error", startErr)
            this.$toast.error("Kamera konnte nicht gestartet werden. Details stehen in der Live-Diagnose.")
            console.error("Admin camera start failed:", startErr)
          }
        })
      } catch (err) {
        if (scannerSessionId !== this.adminScannerSessionId) return
        this.adminScannerStatus = "Initialisierungsfehler"
        this.addAdminScannerLog("ZXing-WASM konnte nicht initialisiert werden", "error", err)
        this.$toast.error("Barcode-Decoder konnte nicht initialisiert werden. Details stehen in der Live-Diagnose.")
        console.error(err)
      }
    },

    closeAdminLiveScanner() {
      this.adminLiveScannerOpen = false
      this.adminScannerSessionId += 1
      this.resetAdminScannerConsensus()
      this.stopAdminCameraStream()
    },

    async openAdminCameraStream() {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Dieser Browser unterstützt keinen Kamerazugriff")
      }

      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        })
      } catch (preferredCameraError) {
        this.addAdminScannerLog("Optimierte Kameraeinstellung nicht verfügbar · kompatibler Fallback wird versucht", "muted", preferredCameraError)
        return navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: "environment" },
        })
      }
    },

    stopAdminCameraStream() {
      if (this.adminScannerTimer) {
        window.clearTimeout(this.adminScannerTimer)
        this.adminScannerTimer = null
      }
      this.adminScannerDecodeBusy = false

      if (this.adminScannerStream) {
        this.adminScannerStream.getTracks().forEach((track) => track.stop())
        this.adminScannerStream = null
      }

      const video = this.$refs.adminScannerVideo
      if (video) video.srcObject = null
    },

    async optimizeAdminScannerCamera(stream) {
      const track = stream?.getVideoTracks?.()[0]
      if (!track || typeof track.getCapabilities !== "function") return

      try {
        const capabilities = track.getCapabilities()
        const settings = typeof track.getSettings === "function" ? track.getSettings() : {}
        const advancedConstraints = {}

        if (Array.isArray(capabilities.focusMode) && capabilities.focusMode.includes("continuous")) {
          advancedConstraints.focusMode = "continuous"
        }

        if (Array.isArray(capabilities.exposureMode) && capabilities.exposureMode.includes("continuous")) {
          advancedConstraints.exposureMode = "continuous"
        }

        if (Array.isArray(capabilities.whiteBalanceMode) && capabilities.whiteBalanceMode.includes("continuous")) {
          advancedConstraints.whiteBalanceMode = "continuous"
        }

        if (
          capabilities.zoom &&
          Number.isFinite(capabilities.zoom.min) &&
          Number.isFinite(capabilities.zoom.max) &&
          capabilities.zoom.max > capabilities.zoom.min
        ) {
          const currentZoom = Number.isFinite(settings.zoom) ? settings.zoom : capabilities.zoom.min
          const preferredZoom = Math.min(capabilities.zoom.max, Math.max(capabilities.zoom.min, currentZoom, 1.15))
          if (preferredZoom > currentZoom) advancedConstraints.zoom = preferredZoom
        }

        if (Object.keys(advancedConstraints).length && typeof track.applyConstraints === "function") {
          await track.applyConstraints({ advanced: [advancedConstraints] })
          const optimizedFeatures = [
            advancedConstraints.focusMode ? "kontinuierlicher Fokus" : null,
            advancedConstraints.exposureMode ? "automatische Belichtung" : null,
            advancedConstraints.whiteBalanceMode ? "automatischer Weissabgleich" : null,
            advancedConstraints.zoom ? `Zoom ${advancedConstraints.zoom.toFixed(2)}×` : null,
          ].filter(Boolean)
          this.addAdminScannerLog(`Kamera optimiert · ${optimizedFeatures.join(" · ")}`, "success")
        } else {
          this.addAdminScannerLog("Gerät bietet keine zusätzlichen Fokus-/Zoom-Steuerungen", "muted")
        }
      } catch (constraintError) {
        this.addAdminScannerLog("Kamera läuft ohne zusätzliche Fokus-/Zoom-Optimierung", "muted", constraintError)
      }
    },

    scheduleAdminWasmScan(scannerSessionId, delay = 70) {
      if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) return
      if (this.adminScannerTimer) window.clearTimeout(this.adminScannerTimer)
      this.adminScannerTimer = window.setTimeout(() => {
        this.adminScannerTimer = null
        this.scanAdminWasmFrame(scannerSessionId)
      }, delay)
    },

    async scanAdminWasmFrame(scannerSessionId) {
      if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) return
      if (this.adminScannerDecodeBusy) {
        this.scheduleAdminWasmScan(scannerSessionId)
        return
      }

      const video = this.$refs.adminScannerVideo
      const canvas = this.$refs.adminScannerCanvas
      if (!video || !canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        this.scheduleAdminWasmScan(scannerSessionId)
        return
      }

      const sourceWidth = video.videoWidth
      const sourceHeight = video.videoHeight
      this.adminScannerFrameIndex += 1
      const shouldRunExtendedPass = Boolean(this.adminScannerCandidate) || this.adminScannerFrameIndex % 3 === 0
      const shouldRunFullPass = Boolean(this.adminScannerCandidate) || this.adminScannerFrameIndex % 3 === 0
      const scanPasses = [
        {
          label: "Fokusbereich",
          x: 0.05,
          y: 0.25,
          width: 0.9,
          height: 0.5,
          binarizer: "LocalAverage",
          maxWidth: 1080,
          readerOptions: {
            tryHarder: true,
            tryRotate: false,
            tryInvert: false,
          },
        },
      ]

      if (shouldRunExtendedPass) {
        scanPasses.push({
          label: "erweiterter Rahmen",
          x: 0.03,
          y: 0.19,
          width: 0.94,
          height: 0.62,
          binarizer: "LocalAverage",
          maxWidth: 1080,
          readerOptions: {
            tryHarder: true,
            tryRotate: true,
            tryInvert: false,
          },
        })
      }

      if (shouldRunFullPass) {
        scanPasses.push({
          label: "Gesamtbild",
          x: 0,
          y: 0,
          width: 1,
          height: 1,
          binarizer: "GlobalHistogram",
          maxWidth: 960,
          readerOptions: {
            tryHarder: true,
            tryRotate: true,
            tryInvert: true,
          },
        })
      }

      this.adminScannerDecodeBusy = true
      try {
        let results = []
        let successfulPass = null

        for (const scanPass of scanPasses) {
          results = await this.decodeAdminVideoRegion(video, canvas, sourceWidth, sourceHeight, scanPass)
          if (Array.isArray(results) && results.length) {
            successfulPass = scanPass.label
            break
          }
        }

        if (!this.adminLiveScannerOpen || scannerSessionId !== this.adminScannerSessionId) return

        if (Array.isArray(results) && results.length) {
          results.forEach((result) => {
            this.registerAdminLiveScanCandidate(
              result.text,
              {
                format: result.format,
                symbology: result.symbology,
                position: result.position,
                scanPass: successfulPass,
              },
              scannerSessionId,
            )
          })
        } else {
          this.registerAdminScanMiss("ZXing-WASM: kein Treffer")
        }
      } catch (decodeError) {
        this.registerAdminScanMiss(decodeError)
      } finally {
        this.adminScannerDecodeBusy = false
        this.scheduleAdminWasmScan(scannerSessionId)
      }
    },

    async decodeAdminVideoRegion(video, canvas, sourceWidth, sourceHeight, scanPass) {
      const sourceX = Math.floor(sourceWidth * scanPass.x)
      const sourceY = Math.floor(sourceHeight * scanPass.y)
      const regionWidth = Math.max(1, Math.floor(sourceWidth * scanPass.width))
      const regionHeight = Math.max(1, Math.floor(sourceHeight * scanPass.height))
      const renderScale = Math.min(1, (scanPass.maxWidth || regionWidth) / regionWidth)
      const renderWidth = Math.max(1, Math.round(regionWidth * renderScale))
      const renderHeight = Math.max(1, Math.round(regionHeight * renderScale))
      canvas.width = renderWidth
      canvas.height = renderHeight

      const context = canvas.getContext("2d", { willReadFrequently: true })
      if (!context) throw new Error("Canvas-Kontext nicht verfügbar")

      context.drawImage(video, sourceX, sourceY, regionWidth, regionHeight, 0, 0, renderWidth, renderHeight)
      const imageData = context.getImageData(0, 0, renderWidth, renderHeight)
      return window.ZXingWASM.readBarcodes(imageData, {
        ...this.getAdminReaderOptions(scanPass.readerOptions),
        binarizer: scanPass.binarizer,
      })
    },

    loadZxingWasm() {
      const configureLocalWasm = () => {
        if (!window.ZXingWASM?.readBarcodes || !window.ZXingWASM?.setZXingModuleOverrides) {
          throw new Error("Die lokale ZXing-WASM-Datei stellt keinen vollständigen Reader bereit")
        }
        window.ZXingWASM.setZXingModuleOverrides(LOCAL_ZXING_MODULE_OVERRIDES)
      }

      if (window.ZXingWASM?.readBarcodes) {
        try {
          configureLocalWasm()
          return Promise.resolve()
        } catch (error) {
          return Promise.reject(error)
        }
      }
      if (window.__lagerZxingWasmLoadingPromise) return window.__lagerZxingWasmLoadingPromise

      window.__lagerZxingWasmLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = LOCAL_BARCODE_SCRIPT_URL
        script.type = "text/javascript"
        script.onload = () => {
          try {
            configureLocalWasm()
            resolve()
          } catch (error) {
            window.__lagerZxingWasmLoadingPromise = null
            reject(error)
          }
        }
        script.onerror = (error) => {
          window.__lagerZxingWasmLoadingPromise = null
          reject(error)
        }
        document.head.appendChild(script)
      })

      return window.__lagerZxingWasmLoadingPromise
    },

    resetAdminScannerConsensus() {
      this.adminScannerCandidate = ""
      this.adminScannerCandidateCount = 0
      this.adminScannerCandidateLastSeenAt = 0
    },

    resetAdminScannerDiagnostics() {
      this.adminScannerStatus = "Bereit"
      this.adminScannerLogs = []
      this.adminScannerFailureCount = 0
      this.adminScannerLastFailureLogAt = 0
      this.adminScannerFrameIndex = 0
    },

    formatAdminScannerError(error) {
      const message = error?.message || error?.name || String(error || "Unbekannter Kamerafehler")
      return message.length > 160 ? `${message.slice(0, 157)}…` : message
    },

    addAdminScannerLog(message, type = "info", details = null) {
      const entry = {
        id: ++this.adminScannerLogId,
        time: new Date().toLocaleTimeString("de-CH", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        message,
        type,
      }
      this.adminScannerLogs = [...this.adminScannerLogs.slice(-7), entry]

      if (type === "error") {
        console.error("[Admin Barcode Scanner]", message, details || "")
      } else if (type === "success") {
        console.info("[Admin Barcode Scanner]", message, details || "")
      } else {
        console.debug("[Admin Barcode Scanner]", message, details || "")
      }
    },

    getAdminDecodedFormat(decodedResult) {
      const format = decodedResult?.result?.format || decodedResult?.format
      if (format?.formatName) return format.formatName
      if (format && typeof format.toString === "function") return format.toString()
      return "Format nicht gemeldet"
    },

    normalizeAdminBarcodeFormat(format) {
      return String(format || "")
        .toUpperCase()
        .replace(/[\s_-]/g, "")
    },

    isValidEan13(barcode) {
      if (!/^\d{13}$/.test(barcode)) return false
      const payload = barcode.slice(0, 12)
      const weightedSum = payload.split("").reduce((sum, digit, index) => sum + Number(digit) * (index % 2 === 0 ? 1 : 3), 0)
      return (10 - (weightedSum % 10)) % 10 === Number(barcode[12])
    },

    registerAdminScanMiss(decodedError) {
      this.adminScannerFailureCount += 1
      const now = Date.now()
      if (this.adminScannerCandidate && now - this.adminScannerCandidateLastSeenAt > 3000) {
        this.resetAdminScannerConsensus()
      }
      if (now - this.adminScannerLastFailureLogAt < 2500) return

      this.adminScannerLastFailureLogAt = now
      this.adminScannerStatus = "Suche läuft"
      this.addAdminScannerLog(`Noch kein lesbarer EAN-13-/Code-128-Treffer · ${this.adminScannerFailureCount} Prüfungen`, "muted", decodedError)
    },

    registerAdminLiveScanCandidate(decodedText, decodedResult, scannerSessionId) {
      const barcode = String(decodedText || "").trim()
      if (!barcode || scannerSessionId !== this.adminScannerSessionId) return

      const reportedFormat = this.getAdminDecodedFormat(decodedResult)
      let normalizedFormat = this.normalizeAdminBarcodeFormat(reportedFormat)
      if (normalizedFormat === "FORMATNICHTGEMELDET" && this.isValidEan13(barcode)) {
        normalizedFormat = "EAN13"
      }

      if (!["EAN13", "CODE128"].includes(normalizedFormat)) {
        this.adminScannerStatus = `Ignoriert: ${reportedFormat}`
        this.addAdminScannerLog(`Treffer ignoriert: ${barcode} · gemeldet als ${reportedFormat}`, "muted", decodedResult)
        return
      }

      if (normalizedFormat === "EAN13" && !this.isValidEan13(barcode)) {
        this.adminScannerStatus = "Ungültige EAN-13"
        this.addAdminScannerLog(`EAN-13 mit ungültiger Prüfziffer ignoriert: ${barcode}`, "error", decodedResult)
        return
      }

      const now = Date.now()
      const belongsToCurrentSequence = barcode === this.adminScannerCandidate && now - this.adminScannerCandidateLastSeenAt <= 3000

      if (belongsToCurrentSequence) {
        this.adminScannerCandidateCount += 1
      } else {
        this.adminScannerCandidate = barcode
        this.adminScannerCandidateCount = 1
      }
      this.adminScannerCandidateLastSeenAt = now
      const format = normalizedFormat === "EAN13" ? "EAN-13" : "Code 128"
      const requiredConfirmations = normalizedFormat === "EAN13" ? 2 : 3
      const scanPass = decodedResult?.scanPass ? ` · ${decodedResult.scanPass}` : ""
      this.adminScannerStatus = `Treffer ${this.adminScannerCandidateCount}/${requiredConfirmations}`
      this.addAdminScannerLog(
        `Erkannt: ${barcode} · ${format}${scanPass} · Bestätigung ${this.adminScannerCandidateCount}/${requiredConfirmations}`,
        "success",
        decodedResult,
      )

      if (this.adminScannerCandidateCount < requiredConfirmations) return

      this.productFormModel.barcode = barcode
      this.adminScannerStatus = "Übernommen"
      this.addAdminScannerLog(`Barcode ${barcode} wurde stabil bestätigt und übernommen`, "success")
      this.closeAdminLiveScanner()
      this.$toast.success("Barcode zuverlässig erkannt und übernommen")
    },

    triggerAdminCaptureFlow() {
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        window.flutter_inappwebview
          .callHandler("openCameraNative")
          .then((result) => {
            if (result && result.success) {
              const file = this.dataURLtoFile(result.base64, result.name || "barcode_capture.jpg")
              this.processAdminBarcodeFromImage(file)
            }
          })
          .catch(() => {
            this.$refs.adminFileInput.click()
          })
      } else {
        this.$refs.adminFileInput.click()
      }
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(",")
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },

    onAdminFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processAdminBarcodeFromImage(file)
      }
      event.target.value = ""
    },

    async processAdminBarcodeFromImage(file) {
      this.scanningFile = true
      try {
        await this.loadZxingWasm()
        const results = await window.ZXingWASM.readBarcodes(file, {
          ...this.getAdminReaderOptions(),
          maxNumberOfSymbols: 1,
        })
        if (!Array.isArray(results) || !results.length) {
          throw new Error("Kein EAN-13- oder Code-128-Barcode erkannt")
        }

        const result = results[0]
        const barcode = String(result.text || "").trim()
        if (this.normalizeAdminBarcodeFormat(result.format) === "EAN13" && !this.isValidEan13(barcode)) {
          throw new Error("EAN-13-Prüfziffer ist ungültig")
        }

        this.productFormModel.barcode = barcode
        this.$toast.success(`Barcode erfolgreich als ${result.format} erkannt`)
      } catch (scanErr) {
        this.$toast.error("Kein gültiger EAN-13- oder Code-128-Barcode erkannt. Bitte deutlicher fotografieren.")
        console.error("ZXing-WASM file scan failed:", scanErr)
      } finally {
        this.scanningFile = false
      }
    },

    // --- EDIT & CANCEL ORDERS (ADMIN) ---
    openEditOrderDialog(order) {
      this.orderToEdit = order
      this.orderFormModel.quantity = order.quantity
      this.editOrderDialogOpen = true
    },

    closeEditOrderDialog() {
      this.editOrderDialogOpen = false
      this.orderToEdit = null
    },

    async saveOrderEdit() {
      if (!this.orderToEdit) return

      this.savingOrderEdit = true
      try {
        await api.marketingStock.updateOrder(this.orderToEdit._id, { quantity: Number(this.orderFormModel.quantity) })
        this.$toast.success("Entnahme erfolgreich aktualisiert")
        this.closeEditOrderDialog()
        await this.loadOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Bearbeiten der Entnahme"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.savingOrderEdit = false
      }
    },

    confirmCancelOrder(order) {
      this.orderToCancel = order
      this.cancelOrderDialogOpen = true
    },

    closeCancelOrderDialog() {
      this.cancelOrderDialogOpen = false
      this.orderToCancel = null
    },

    async executeCancelOrder() {
      if (!this.orderToCancel) return

      this.cancelingOrder = true
      try {
        await api.marketingStock.deleteOrder(this.orderToCancel._id)
        this.$toast.success("Entnahme erfolgreich storniert")
        this.closeCancelOrderDialog()
        await this.loadOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Stornieren der Entnahme"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.cancelingOrder = false
      }
    },
  },
}
</script>

<style scoped>
.stock-admin-shell {
  --ops-bg: #1f201f;
  --ops-surface: #252625;
  --ops-surface-raised: #2a2b2a;
  --ops-surface-soft: #1b1c1b;
  --ops-border: #3a3c3a;
  --ops-border-soft: #303230;
  --ops-text: #fbfbfb;
  --ops-text-secondary: #b6b8b6;
  --ops-text-muted: #919491;
  --ops-accent: #42944a;
  --ops-accent-strong: #347a3b;
  --ops-accent-soft: rgba(66, 148, 74, 0.14);
  --ops-success: #8fd096;
  --ops-warning: #ffd58a;
  --ops-danger: #ffb4b8;
  min-height: 100%;
  padding-top: max(16px, env(safe-area-inset-top));
  padding-right: max(16px, env(safe-area-inset-right));
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  padding-left: max(16px, env(safe-area-inset-left));
  background: radial-gradient(circle at 8% -10%, rgba(66, 148, 74, 0.12), transparent 32rem), var(--ops-bg);
  color: var(--ops-text);
}

.stock-admin-container {
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
}

.operations-header {
  position: relative;
  overflow: hidden;
  padding: 26px;
  border: 1px solid var(--ops-border);
  border-radius: 24px;
  background: linear-gradient(115deg, rgba(66, 148, 74, 0.07), transparent 42%), var(--ops-surface);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.24);
}

.operations-header::after {
  position: absolute;
  top: -140px;
  right: -100px;
  width: 360px;
  height: 360px;
  border: 1px solid rgba(66, 148, 74, 0.13);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(66, 148, 74, 0.1), transparent 66%);
  content: "";
  pointer-events: none;
}

.operations-header__top {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
}

.operations-header__copy {
  min-width: 0;
}

.eyebrow,
.section-kicker,
.dialog-eyebrow {
  margin: 0;
  color: var(--ops-accent);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.4;
  text-transform: uppercase;
}

.eyebrow {
  display: flex;
  gap: 9px;
  align-items: center;
}

.eyebrow__mark {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ops-accent);
  box-shadow: 0 0 0 5px rgba(66, 148, 74, 0.14);
}

.operations-title {
  max-width: 780px;
  margin: 8px 0 0;
  color: var(--ops-text);
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 760;
  letter-spacing: -0.045em;
  line-height: 1.02;
}

.operations-subtitle {
  max-width: 640px;
  margin: 10px 0 0;
  color: var(--ops-text-secondary);
  font-size: 0.98rem;
  line-height: 1.55;
}

.secondary-action,
.primary-action,
.mobile-action-button,
.dialog-primary-button,
.dialog-secondary-button,
.dialog-danger-button,
.reset-filter-button {
  min-height: 44px;
  border-radius: 12px !important;
  font-weight: 750 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.secondary-action {
  border-color: #4a4d4a !important;
  color: #f0f2f0 !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.header-back-button {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
}

.kpi-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 24px 0 0;
}

.kpi-card {
  position: relative;
  min-width: 0;
  padding: 15px 16px;
  overflow: hidden;
  border: 1px solid var(--ops-border-soft);
  border-radius: 16px;
  background: rgba(8, 12, 16, 0.5);
}

.kpi-card::before {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 3px;
  background: #717471;
  content: "";
}

.kpi-card--active::before {
  background: #42944a;
}

.kpi-card--low::before {
  background: #f6b94d;
}

.kpi-card--empty::before {
  background: #f06d76;
}

.kpi-card__label {
  color: var(--ops-text-muted);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.kpi-card__value {
  margin: 4px 0 0;
  color: var(--ops-text);
  font-size: 1.6rem;
  font-variant-numeric: tabular-nums;
  font-weight: 780;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.kpi-card__meta {
  display: block;
  margin-top: 3px;
  overflow: hidden;
  color: var(--ops-text-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.segmented-nav {
  width: 100%;
  max-width: 620px;
  margin: 18px 0;
  padding: 5px;
  border: 1px solid var(--ops-border);
  border-radius: 16px;
  background: #1b1c1b;
}

.operations-tabs >>> .v-tabs-bar {
  height: 46px;
  background: transparent !important;
}

.operations-tabs >>> .v-slide-group__content {
  gap: 4px;
}

.operations-tab {
  min-width: 0 !important;
  min-height: 44px;
  border-radius: 11px;
  color: var(--ops-text-secondary) !important;
  font-size: 0.88rem;
  font-weight: 720;
  letter-spacing: 0 !important;
  text-transform: none !important;
  transition: background-color 180ms ease, color 180ms ease, box-shadow 180ms ease;
}

.operations-tab::before {
  display: none;
}

.operations-tab.v-tab--active {
  color: #f8faf8 !important;
  background: #29352b;
  box-shadow: inset 0 0 0 1px rgba(66, 148, 74, 0.34), 0 4px 12px rgba(0, 0, 0, 0.2);
}

.operations-tab >>> .v-icon {
  color: currentColor !important;
}

.tab-count,
.filter-count {
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  margin-left: 9px;
  padding: 0 7px;
  align-items: center;
  justify-content: center;
  border: 1px solid #464946;
  border-radius: 999px;
  color: var(--ops-text-secondary);
  background: rgba(0, 0, 0, 0.16);
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.operations-tab.v-tab--active .tab-count {
  border-color: rgba(66, 148, 74, 0.46);
  color: #d7ead9;
  background: rgba(66, 148, 74, 0.15);
}

.operations-tab-items {
  width: 100%;
  overflow: visible;
  background: transparent !important;
}

.operations-tab-items >>> .v-window__container,
.operations-tab-items >>> .v-window-item {
  overflow: visible;
}

.content-panel {
  padding: 24px;
  border: 1px solid var(--ops-border);
  border-radius: 24px;
  background: var(--ops-surface);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.18);
}

.section-heading-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title {
  margin: 3px 0 0;
  color: var(--ops-text);
  font-size: clamp(1.3rem, 2vw, 1.65rem);
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.result-count {
  color: var(--ops-text-muted);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
}

.product-toolbar,
.history-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.operations-search {
  width: min(100%, 480px);
  max-width: 480px;
  flex: 1 1 320px;
}

.operations-search >>> .v-input__slot {
  min-height: 48px !important;
  margin: 0;
  border: 1px solid var(--ops-border) !important;
  border-radius: 13px !important;
  background: var(--ops-surface-soft) !important;
  box-shadow: none !important;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.operations-search >>> .v-input__slot:hover {
  border-color: #505350 !important;
  background: #292a29 !important;
}

.operations-search >>> .v-label,
.operations-search >>> input {
  color: var(--ops-text) !important;
}

.operations-search >>> input::placeholder {
  color: #919491;
  opacity: 1;
}

.operations-search >>> .v-icon {
  color: #a3a6a3 !important;
}

.primary-action,
.dialog-primary-button {
  color: #ffffff !important;
  background: var(--ops-accent) !important;
  box-shadow: 0 7px 18px rgba(66, 148, 74, 0.24) !important;
}

@media (hover: hover) {
  .primary-action:hover,
  .dialog-primary-button:hover {
    background: #4b9f53 !important;
  }
}

.product-create-button {
  min-width: 172px !important;
  min-height: 48px;
  flex: 0 0 auto;
}

.operations-table {
  overflow: hidden;
  border: 1px solid var(--ops-border-soft);
  border-radius: 16px;
  background: var(--ops-surface-soft) !important;
}

.operations-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.operations-table >>> table {
  min-width: 960px;
}

.operations-table >>> thead th {
  height: 46px;
  border-bottom: 1px solid var(--ops-border) !important;
  color: #a2a5a2 !important;
  background: #292a29;
  font-size: 0.68rem !important;
  font-weight: 820 !important;
  letter-spacing: 0.085em;
  text-transform: uppercase;
  white-space: nowrap;
}

.operations-table >>> tbody td {
  height: 64px;
  border-bottom: 1px solid var(--ops-border-soft) !important;
  color: var(--ops-text-secondary);
  font-size: 0.82rem;
}

.operations-table >>> tbody tr:last-child td {
  border-bottom: 0 !important;
}

.operations-table >>> tbody tr:hover {
  background: rgba(66, 148, 74, 0.07) !important;
}

.operations-table >>> .v-data-footer {
  min-height: 54px;
  border-top: 1px solid var(--ops-border) !important;
  color: var(--ops-text-secondary);
  background: #292a29;
}

.operations-table >>> .v-data-footer .v-icon {
  color: var(--ops-text-secondary) !important;
}

.table-primary-text {
  color: var(--ops-text);
  font-weight: 680;
}

.barcode-value {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  color: #a6aaa6;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 0.73rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.barcode-value >>> .v-icon {
  color: #838683 !important;
}

.product-cell,
.notification-cell {
  display: flex;
  flex-direction: column;
}

.notification-cell {
  gap: 2px;
  color: var(--ops-text-secondary);
  font-size: 0.73rem;
}

.notification-cell span:last-child {
  max-width: 180px;
  overflow: hidden;
  color: var(--ops-text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-muted,
.comment-cell {
  color: var(--ops-text-muted);
  font-size: 0.76rem;
}

.comment-cell {
  display: inline-block;
  max-width: 210px;
  overflow: hidden;
  color: var(--ops-text-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-cell {
  color: var(--ops-text-secondary);
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.status-label,
.quantity-label {
  display: inline-flex;
  min-height: 26px;
  padding: 4px 9px;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.69rem;
  font-variant-numeric: tabular-nums;
  font-weight: 820;
  letter-spacing: 0.015em;
  line-height: 1.2;
  white-space: nowrap;
}

.status-label--active,
.status-label--available {
  border-color: rgba(66, 148, 74, 0.48);
  color: #d5ead7;
  background: rgba(66, 148, 74, 0.16);
}

.status-label--low {
  border-color: rgba(246, 185, 77, 0.43);
  color: var(--ops-warning);
  background: rgba(153, 98, 7, 0.23);
}

.status-label--empty {
  border-color: rgba(240, 109, 118, 0.42);
  color: var(--ops-danger);
  background: rgba(151, 42, 50, 0.24);
}

.status-label--inactive,
.status-label--unlimited {
  border-color: #555855;
  color: #d5d8d5;
  background: rgba(87, 101, 115, 0.18);
}

.quantity-label {
  min-width: 32px;
  border-color: rgba(66, 148, 74, 0.45);
  color: #d5ead7;
  background: var(--ops-accent-soft);
}

.table-actions,
.mobile-card-actions,
.barcode-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.table-actions {
  justify-content: flex-end;
}

.touch-icon-button {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  color: #c0c4c0 !important;
  background: transparent !important;
}

.touch-icon-button:hover {
  color: #edf5ee !important;
  background: rgba(66, 148, 74, 0.16) !important;
}

.touch-icon-button--danger {
  color: var(--ops-danger) !important;
}

.touch-icon-button--danger:hover {
  background: rgba(240, 109, 118, 0.11) !important;
}

.mobile-card-list {
  display: none;
}

.history-toolbar {
  justify-content: flex-start;
}

.history-search {
  margin-right: auto;
}

.reset-filter-button {
  flex: 0 0 auto;
  color: #d7ead9 !important;
}

.reset-filter-button:hover {
  background: var(--ops-accent-soft) !important;
}

.filter-count {
  border-color: rgba(66, 148, 74, 0.42);
  color: #dff0e1;
}

.filter-panel {
  margin-bottom: 18px;
  padding: 15px;
  border: 1px solid var(--ops-border-soft);
  border-radius: 16px;
  background: var(--ops-surface-soft);
}

.filter-panel__label {
  display: flex;
  gap: 7px;
  align-items: center;
  margin-bottom: 12px;
  color: #b4b8b4;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.filter-panel__label >>> .v-icon {
  color: var(--ops-accent) !important;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.operations-select >>> .v-input__slot {
  min-height: 44px !important;
  border-color: #464946 !important;
  border-radius: 11px !important;
  background: #292a29 !important;
}

.operations-select >>> fieldset {
  border-color: #464946 !important;
}

.operations-select.v-input--is-focused >>> fieldset {
  border-color: #42944a !important;
  border-width: 2px !important;
}

.operations-select >>> .v-label,
.operations-select >>> .v-select__selection,
.operations-select >>> .v-icon {
  color: #d9dcd9 !important;
}

.dialog-card {
  --ops-border-soft: #303230;
  --ops-text: #f4f6f4;
  --ops-text-secondary: #b6b8b6;
  --ops-text-muted: #919491;
  --ops-accent: #42944a;
  --ops-accent-soft: rgba(66, 148, 74, 0.14);
  --ops-danger: #ffb4b8;
  overflow: hidden;
  border: 1px solid #464946 !important;
  border-radius: 22px !important;
  color: var(--ops-text) !important;
  background: #252625 !important;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.62) !important;
}

.product-form-card {
  max-height: calc(100vh - 48px);
}

.dialog-header {
  display: flex;
  min-height: 82px;
  padding: 18px 22px !important;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #3a3c3a;
  background: #2b2c2b;
}

.dialog-title-group,
.confirmation-header {
  display: flex;
  min-width: 0;
  gap: 13px;
  align-items: center;
}

.dialog-icon,
.confirmation-icon {
  display: inline-flex;
  width: 44px;
  min-width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(66, 148, 74, 0.4);
  border-radius: 13px;
  color: #d5ead7;
  background: var(--ops-accent-soft);
}

.dialog-icon >>> .v-icon,
.confirmation-icon >>> .v-icon {
  color: currentColor !important;
}

.dialog-title {
  margin: 2px 0 0;
  color: var(--ops-text);
  font-size: 1.16rem;
  font-weight: 760;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.dialog-close-button {
  margin-left: 12px;
}

.product-dialog-body,
.compact-form-body {
  padding: 20px 22px 28px !important;
  background: #1b1c1b;
}

.form-section {
  padding: 20px;
  border: 1px solid #3a3c3a;
  border-radius: 16px;
  background: #292a29;
}

.form-section + .form-section {
  margin-top: 14px;
}

.form-section__header {
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  align-items: flex-start;
}

.form-section__number {
  display: inline-flex;
  min-width: 29px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(66, 148, 74, 0.42);
  border-radius: 7px;
  color: #c5dfc8;
  background: var(--ops-accent-soft);
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 0.65rem;
  font-weight: 800;
}

.form-section__header h3 {
  margin: 0;
  color: var(--ops-text);
  font-size: 0.94rem;
  font-weight: 760;
  line-height: 1.25;
}

.form-section__header p {
  margin: 3px 0 0;
  color: var(--ops-text-muted);
  font-size: 0.77rem;
  line-height: 1.45;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 12px;
}

.form-grid--spaced {
  margin-top: 16px;
}

.form-grid__full,
.switch-row--nested {
  grid-column: 1 / -1;
}

.form-field >>> .v-input__slot {
  border-color: #464946 !important;
  border-radius: 12px !important;
  background: #202120 !important;
}

.form-field >>> fieldset {
  border-color: #464946 !important;
}

.form-field.v-input--is-focused >>> fieldset {
  border-color: #42944a !important;
  border-width: 2px !important;
}

.form-field >>> .v-input__slot:hover {
  border-color: #646764 !important;
}

.form-field >>> .v-label,
.form-field >>> input,
.form-field >>> .v-select__selection,
.form-field >>> .v-icon {
  color: #e5e8e5 !important;
}

.form-field >>> .v-messages__message {
  color: #aaadaa;
  line-height: 1.4;
}

.barcode-field-row {
  display: flex;
  min-width: 0;
  gap: 8px;
  align-items: flex-start;
}

.barcode-input {
  min-width: 0;
  flex: 1 1 auto;
}

.barcode-actions {
  padding-top: 5px;
  flex: 0 0 auto;
}

.field-action-button {
  border: 1px solid #464946;
  border-radius: 12px;
  background: #2d2e2d !important;
}

.field-action-button--accent {
  border-color: rgba(66, 148, 74, 0.44);
  color: #d5ead7 !important;
  background: var(--ops-accent-soft) !important;
}

.visually-hidden-input {
  display: none;
}

.admin-scanner-card {
  display: flex;
  max-height: calc(100vh - 48px);
  flex-direction: column;
}

.admin-scanner-body {
  padding: 20px 22px 24px !important;
  overflow-y: auto;
  background: #1b1c1b;
}

.admin-scanner-container {
  position: relative;
  height: min(40vh, 320px);
  min-height: 220px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  background: #070807;
}

.admin-live-qr-reader {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.admin-scanner-frame {
  position: absolute;
  z-index: 3;
  top: 50%;
  left: 50%;
  width: 90%;
  height: 50%;
  border: 2px solid rgba(213, 234, 215, 0.82);
  border-radius: 16px;
  box-shadow: 0 0 0 999px rgba(7, 8, 7, 0.42), inset 0 0 24px rgba(66, 148, 74, 0.12);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.admin-scanner-laser {
  position: absolute;
  z-index: 4;
  top: 22%;
  left: 5%;
  width: 90%;
  height: 2px;
  border-radius: 2px;
  background: #63ad6b;
  box-shadow: 0 0 12px rgba(66, 148, 74, 0.82);
  animation: admin-scanning 2.4s ease-in-out infinite;
  pointer-events: none;
}

.admin-scanner-tip {
  display: flex;
  gap: 9px;
  margin-top: 14px;
  padding: 11px 12px;
  align-items: flex-start;
  border: 1px solid rgba(66, 148, 74, 0.26);
  border-radius: 11px;
  color: var(--ops-text-secondary);
  background: rgba(66, 148, 74, 0.09);
  font-size: 0.77rem;
  line-height: 1.5;
}

.admin-scanner-tip >>> .v-icon {
  margin-top: 1px;
  color: var(--ops-accent) !important;
}

.admin-scanner-debug {
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid #3a3c3a;
  border-radius: 12px;
  background: #202120;
}

.admin-scanner-debug__header {
  display: flex;
  min-height: 38px;
  gap: 10px;
  padding: 8px 11px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #3a3c3a;
  color: var(--ops-text-secondary);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.admin-scanner-status {
  display: inline-flex;
  min-height: 22px;
  padding: 3px 8px;
  align-items: center;
  border: 1px solid rgba(66, 148, 74, 0.36);
  border-radius: 999px;
  color: #d5ead7;
  background: rgba(66, 148, 74, 0.13);
  font-size: 0.62rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  text-transform: none;
}

.admin-scanner-log {
  display: grid;
  max-height: 148px;
  margin: 0;
  padding: 5px 0;
  overflow-y: auto;
  list-style: none;
}

.admin-scanner-log li {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 8px;
  padding: 5px 11px;
  color: var(--ops-text-secondary);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 0.67rem;
  line-height: 1.4;
}

.admin-scanner-log time {
  color: var(--ops-text-muted);
  font-variant-numeric: tabular-nums;
}

.admin-scanner-log__entry--success span {
  color: #d5ead7;
}

.admin-scanner-log__entry--error span {
  color: var(--ops-danger);
}

.admin-scanner-log__entry--muted span {
  color: var(--ops-text-muted);
}

@keyframes admin-scanning {
  0%,
  100% {
    top: 27%;
    opacity: 0.55;
  }
  50% {
    top: 73%;
    opacity: 1;
  }
}

.switch-row {
  display: flex;
  min-height: 58px;
  gap: 14px;
  padding: 10px 12px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #3c3e3c;
  border-radius: 13px;
  background: #202120;
}

.switch-row--nested {
  margin-bottom: 14px;
}

.switch-row strong,
.switch-row span {
  display: block;
}

.switch-row strong {
  color: #e8ebe8;
  font-size: 0.82rem;
  font-weight: 720;
}

.switch-row span {
  margin-top: 2px;
  color: var(--ops-text-muted);
  font-size: 0.72rem;
  line-height: 1.4;
}

.switch-row >>> .v-input--switch {
  margin: 0;
  flex: 0 0 auto;
}

.dialog-actions {
  display: flex;
  min-height: 72px;
  gap: 8px;
  padding: 12px 22px max(12px, env(safe-area-inset-bottom)) !important;
  justify-content: flex-end;
  border-top: 1px solid #3a3c3a;
  background: #2b2c2b;
}

.dialog-actions--sticky {
  position: sticky;
  z-index: 5;
  bottom: 0;
}

.dialog-secondary-button,
.dialog-primary-button,
.dialog-danger-button {
  min-height: 44px;
  padding: 0 18px !important;
}

.dialog-secondary-button {
  color: #c9ccc9 !important;
}

.dialog-danger-button {
  color: #180507 !important;
  background: #f47b83 !important;
}

.confirmation-card {
  padding-top: 4px;
}

.confirmation-header {
  padding: 22px 22px 12px !important;
}

.confirmation-icon--warning {
  border-color: rgba(246, 185, 77, 0.38);
  color: #ffdc9b;
  background: rgba(153, 98, 7, 0.2);
}

.confirmation-icon--danger {
  border-color: rgba(240, 109, 118, 0.42);
  color: #ffc1c4;
  background: rgba(151, 42, 50, 0.23);
}

.confirmation-copy {
  padding: 8px 22px 22px !important;
  color: var(--ops-text-secondary) !important;
  font-size: 0.88rem;
  line-height: 1.65;
}

.confirmation-copy strong {
  color: var(--ops-text);
}

.confirmation-actions {
  min-height: 70px;
}

.compact-form-card {
  min-height: 0;
}

.compact-form-body {
  padding-top: 22px !important;
  min-height: 0;
  overflow-y: auto;
}

.order-summary {
  display: flex;
  margin-bottom: 18px;
  padding: 13px 14px;
  flex-direction: column;
  border: 1px solid var(--ops-border-soft);
  border-radius: 12px;
  background: #292a29;
}

.order-summary span {
  color: var(--ops-text);
  font-weight: 720;
}

.order-summary small {
  margin-top: 3px;
  color: var(--ops-text-muted);
}

.dialog-note {
  display: flex;
  gap: 7px;
  margin: -4px 0 0;
  color: var(--ops-text-muted);
  font-size: 0.75rem;
  line-height: 1.45;
}

.dialog-note >>> .v-icon {
  color: var(--ops-accent) !important;
}

.stock-admin-shell >>> .v-btn:focus,
.stock-admin-shell >>> input:focus,
.stock-admin-shell >>> [role="tab"]:focus,
.dialog-card >>> .v-btn:focus,
.dialog-card >>> input:focus {
  outline: 2px solid rgba(92, 168, 100, 0.8) !important;
  outline-offset: 2px !important;
}

.stock-admin-shell >>> .v-btn:focus-visible,
.stock-admin-shell >>> input:focus-visible,
.stock-admin-shell >>> [role="tab"]:focus-visible,
.dialog-card >>> .v-btn:focus-visible,
.dialog-card >>> input:focus-visible {
  outline: 3px solid rgba(92, 168, 100, 0.96) !important;
  outline-offset: 3px !important;
}

@media (max-width: 1199px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1100px) {
  .desktop-table {
    display: block;
  }

  .mobile-card-list {
    display: none;
  }
}

@media (max-width: 1099px) {
  .desktop-table {
    display: none;
  }

  .mobile-card-list {
    display: grid;
    gap: 12px;
  }

  .mobile-data-card {
    padding: 17px;
    border: 1px solid var(--ops-border-soft);
    border-radius: 16px;
    background: var(--ops-surface-soft);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
  }

  .mobile-data-card__header {
    display: flex;
    min-width: 0;
    gap: 14px;
    align-items: flex-start;
    justify-content: space-between;
  }

  .mobile-data-card__title-wrap {
    min-width: 0;
  }

  .mobile-data-card__eyebrow {
    display: block;
    margin-bottom: 4px;
    overflow: hidden;
    color: var(--ops-accent);
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .mobile-data-card__title {
    margin: 0 0 5px;
    color: var(--ops-text);
    font-size: 1rem;
    font-weight: 740;
    letter-spacing: -0.015em;
    line-height: 1.35;
  }

  .mobile-data-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
    margin: 16px 0 0;
  }

  .mobile-data-grid--orders {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .mobile-data-grid > div {
    min-width: 0;
    padding: 10px;
    border: 1px solid #333533;
    border-radius: 11px;
    background: #292a29;
  }

  .mobile-data-grid dt {
    margin-bottom: 5px;
    color: var(--ops-text-muted);
    font-size: 0.63rem;
    font-weight: 800;
    letter-spacing: 0.075em;
    text-transform: uppercase;
  }

  .mobile-data-grid dd {
    margin: 0;
    overflow-wrap: anywhere;
    color: #e1e4e1;
    font-size: 0.77rem;
    font-weight: 650;
    line-height: 1.4;
  }

  .mobile-comment {
    margin: 10px 0 0;
    padding: 10px 11px;
    border-left: 2px solid rgba(66, 148, 74, 0.58);
    border-radius: 0 8px 8px 0;
    color: var(--ops-text-secondary);
    background: rgba(66, 148, 74, 0.08);
    font-size: 0.76rem;
    line-height: 1.5;
  }

  .mobile-card-actions {
    margin: 13px -5px -5px 0;
    justify-content: flex-end;
  }

  .mobile-action-button {
    color: #d5ead7 !important;
  }

  .loading-state,
  .empty-state {
    display: flex;
    min-height: 190px;
    padding: 28px 20px;
    align-items: center;
    justify-content: center;
    border: 1px dashed #464946;
    border-radius: 16px;
    color: var(--ops-text-secondary);
    background: var(--ops-surface-soft);
    text-align: center;
  }

  .loading-state {
    gap: 12px;
    flex-direction: row;
    font-size: 0.83rem;
  }

  .empty-state {
    gap: 7px;
    flex-direction: column;
  }

  .empty-state >>> .v-icon {
    margin-bottom: 4px;
    color: #767a76 !important;
    font-size: 34px !important;
  }

  .empty-state strong {
    color: var(--ops-text);
    font-size: 0.92rem;
  }

  .empty-state span {
    max-width: 340px;
    color: var(--ops-text-muted);
    font-size: 0.77rem;
    line-height: 1.5;
  }
}

@media (max-width: 719px) {
  .operations-header__top {
    flex-direction: column;
  }

  .header-back-button {
    align-self: stretch;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .stock-admin-shell {
    padding-top: max(10px, env(safe-area-inset-top));
    padding-right: max(10px, env(safe-area-inset-right));
    padding-bottom: max(18px, env(safe-area-inset-bottom));
    padding-left: max(10px, env(safe-area-inset-left));
  }

  .operations-header {
    padding: 18px;
    border-radius: 20px;
  }

  .operations-title {
    font-size: 2rem;
  }

  .operations-subtitle {
    font-size: 0.88rem;
  }

  .kpi-grid {
    margin-top: 19px;
  }

  .kpi-card {
    padding: 12px;
    border-radius: 13px;
  }

  .kpi-card__value {
    font-size: 1.35rem;
  }

  .segmented-nav {
    max-width: none;
    margin: 12px 0;
    border-radius: 14px;
  }

  .operations-tab {
    padding: 0 8px;
    font-size: 0.79rem;
  }

  .operations-tab >>> .v-icon {
    margin-right: 6px !important;
    font-size: 19px !important;
  }

  .tab-count {
    min-width: 20px;
    height: 20px;
    margin-left: 6px;
    padding: 0 5px;
  }

  .content-panel {
    padding: 16px;
    border-radius: 20px;
  }

  .section-heading-row {
    margin-bottom: 16px;
  }

  .product-toolbar,
  .history-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .operations-search,
  .history-search {
    width: 100%;
    max-width: none;
    flex: 0 0 auto;
    margin-right: 0;
  }

  .product-create-button,
  .reset-filter-button {
    width: 100%;
  }

  .filter-panel {
    padding: 12px;
  }

  .filter-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .mobile-data-card {
    padding: 15px;
  }

  .mobile-data-card__header {
    gap: 9px;
  }

  .mobile-data-grid--orders {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .mobile-data-grid--orders > div:first-child {
    grid-column: 1 / -1;
  }

  .product-form-card,
  .compact-form-card,
  .admin-scanner-card {
    width: 100%;
    height: 100%;
    max-height: none;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-header {
    min-height: 76px;
    padding-top: max(14px, env(safe-area-inset-top)) !important;
    padding-right: max(14px, env(safe-area-inset-right)) !important;
    padding-left: max(14px, env(safe-area-inset-left)) !important;
  }

  .dialog-icon {
    width: 40px;
    min-width: 40px;
    height: 40px;
  }

  .dialog-title {
    font-size: 1.05rem;
  }

  .product-dialog-body,
  .compact-form-body {
    padding: 14px 12px 24px !important;
  }

  .admin-scanner-body {
    display: flex;
    min-height: 0;
    padding: 14px 12px max(16px, env(safe-area-inset-bottom)) !important;
    flex: 1 1 auto;
    flex-direction: column;
  }

  .admin-scanner-container {
    height: min(38vh, 280px);
    min-height: 210px;
    flex: 0 0 auto;
  }

  .form-section {
    padding: 15px;
    border-radius: 14px;
  }

  .form-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .form-grid__full,
  .switch-row--nested {
    grid-column: auto;
  }

  .barcode-field-row {
    gap: 5px;
  }

  .barcode-actions {
    gap: 2px;
  }

  .switch-row {
    padding: 10px;
  }

  .dialog-actions {
    min-height: 0;
    padding-right: max(12px, env(safe-area-inset-right)) !important;
    padding-left: max(12px, env(safe-area-inset-left)) !important;
    align-items: stretch;
    flex-direction: column;
  }

  .dialog-actions .v-btn {
    width: 100%;
    min-height: 46px;
    margin: 0 !important;
  }

  .confirmation-card {
    border-radius: 18px !important;
  }

  .confirmation-header {
    padding: 19px 17px 10px !important;
  }

  .confirmation-copy {
    padding: 8px 17px 18px !important;
  }
}

@media (max-width: 374px) {
  .operations-tab .tab-count {
    display: none;
  }

  .kpi-card__meta {
    font-size: 0.68rem;
  }

  .mobile-data-grid,
  .mobile-data-grid--orders {
    grid-template-columns: minmax(0, 1fr);
  }

  .mobile-data-grid--orders > div:first-child {
    grid-column: auto;
  }

  .barcode-field-row {
    align-items: stretch;
    flex-direction: column;
  }

  .barcode-actions {
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stock-admin-shell *,
  .stock-admin-shell *::before,
  .stock-admin-shell *::after,
  .dialog-card,
  .dialog-card * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>

<style>
.stock-admin-menu {
  overflow: hidden;
  border: 1px solid #464946;
  border-radius: 13px;
  background: #292a29 !important;
  box-shadow: 0 20px 54px rgba(0, 0, 0, 0.48) !important;
}

.stock-admin-menu .v-list {
  padding: 6px;
  background: #292a29 !important;
}

.stock-admin-menu .v-list-item {
  min-height: 44px;
  border-radius: 9px;
  color: #e3e6e3 !important;
}

.stock-admin-menu .v-list-item--active,
.stock-admin-menu .v-list-item:hover {
  color: #edf5ee !important;
  background: rgba(66, 148, 74, 0.16) !important;
}

@media (prefers-reduced-motion: reduce) {
  .dialog-bottom-transition-enter-active,
  .dialog-bottom-transition-leave-active,
  .dialog-transition-enter-active,
  .dialog-transition-leave-active {
    transition-duration: 0.01ms !important;
    animation: none !important;
  }
}
</style>
